import { Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, signal, TemplateRef, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ADPConfigService, ADPResourceService,  ADPComponentsModule } from 'digital-core';
import { AccountService, GoogleAnalyticsService, LoginService, UtilityService, PreInitSettingsService, StorageService, PGHAPIService } from 'src/app/core/core';
import { GTMLoginEvents, GTMPasswordResetEvents } from 'src/app/model/google-tag-manager/api-status';
import { INotFoundModal } from 'src/app/model/sitefinity/LoginPage';
import { dashboardRouteWithSlash, myAccountRouteWithSlash, underMaintenanceRoute } from 'src/app/core/core';
import { FailedLoginModalComponent } from '../failed-login-modal/failed-login-modal.component';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import format from 'date-fns/format';
import { ADPImageComponent, GtmClickDirective, SettingTrueDirective } from 'src/app/shared';
import { AutofillMonitor } from '@angular/cdk/text-field';
import { MaintenanceBannerComponent } from '../maintenance-banner/maintenance-banner.component';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [ADPImageComponent, ADPComponentsModule, GtmClickDirective, MaintenanceBannerComponent, SettingTrueDirective]
})
export class LoginComponent implements OnInit, OnDestroy {
  signIn: InstanceType<Window['OktaSignIn']>;

  readonly failedLoginModal = viewChild<TemplateRef<any>>('failedLoginModal');
  private configSettings;
  private oktaPrefix: any;
  private checkInterval: any;
  private resetEmailPollInterval: any;
  // Tracks the last value we saw in #okta-signin-username so replaceUsername()
  // only acts when Okta actually changes the field (screen re-render), not on
  // every MutationObserver tick — see monitorFormErrors().
  private lastUsernameFieldValue = '';
  redirectToMaintenancePage: boolean;
  createAccountRoute = signal('');
  hideMainImageOnMobile = signal(false);
  isLoading = true;
  private usernameAutofilled = false;
  private passwordAutofilled = false;
  private usernameAutofillSubscription: any;
  private passwordAutofillSubscription: any;

  private waitForOktaScripts(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 100; // 5 seconds maximum wait time
      const checkScripts = () => {
        if (window.OktaSignIn) {
          resolve();
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          reject(new Error('Okta Sign-In Widget scripts failed to load'));
          return;
        }
        console.log('Checking');
        setTimeout(checkScripts, 50); // Check every 500ms
      };

      checkScripts();
    });
  }


  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private preInitAppSettings: PreInitSettingsService,
    private loginService: LoginService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private matDialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService,
    private resourceService: ADPResourceService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private storageService: StorageService,
    private accountService: AccountService,
    public pghApiService: PGHAPIService,
    private configService: ADPConfigService,
    private httpClient: HttpClient,
    private autofillMonitor: AutofillMonitor
  ) {
    this.configSettings = this.preInitAppSettings.getSettings();
  }

  async ngOnInit() {
    this.hideMainImageOnMobile.set(this.resourceService.getPortalSetting('Login_Main_Image_Mobile_Hidden_Enabled')?.Value === 'true');
    this.stripHeaderKeyFromUrl();
    if (this.utilityService.isNativeApp && document.querySelector<HTMLElement>(".login-container.container-fluid")) {
      document.querySelector<HTMLElement>(".login-container.container-fluid").style.display = 'none';
    }
    this.redirectToMaintenancePage = this.resourceService.isPortalSettingTrue("Global_Is_Portal_Under_Maintenance");
    if (this.redirectToMaintenancePage) {
      this.router.navigate([underMaintenanceRoute]);
    }
    this.isLoading = true;
    await this.waitForOktaScripts();
    this.loginService.selectedContractId = undefined;
    this.loginService.selectedPlanNumber = undefined;

    const oktaSessionExists = false;
    const selectedSiteSetting = this.preInitAppSettings.getSiteSettings();
    const oktTokenStorage = localStorage.getItem('okta-token-storage');
    if (oktaSessionExists || oktTokenStorage) {
      const tokenDetail = oktaSessionExists ? await this.getToken()
        : (oktTokenStorage ? { isValid: this.isValidateToken(oktTokenStorage), token: JSON.parse(oktTokenStorage) }
          : { isValid: false, token: null });

      if (tokenDetail.isValid) {
        const tokens = tokenDetail.token;
        const idToken = oktaSessionExists ? tokens.tokens.idToken
          : (tokens.id_token ? tokens.id_token : tokens.idToken);

        const accessToken = oktaSessionExists ? tokens.tokens.accessToken
          : (tokens.access_token ? tokens.access_token : tokens.accessToken);

        const token = oktaSessionExists ? tokens.tokens.accessToken.accessToken
          : (tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
            : tokens.accessToken.accessToken);

        const decodedToken = this.oktaAuth.token.decode(token);
        let isExists = true; // All users are part of the same group by default
        // Check exist in group or not
        // if (decodedToken && decodedToken.payload && Array.isArray(decodedToken.payload.groups)) {
        //   // Check if groups array has length and perform further operations
        //   if (decodedToken.payload.groups.length > 0) {
        //     isExists = decodedToken.payload.groups.some(x =>
        //       x.toString().toLowerCase().trim() === this.resourceService.getPortalSetting('Global_SiteSettings_GroupName').Value.toLowerCase().trim()
        //     );
        //   }
        // }

        let isTokenExpired = false;
        if (decodedToken?.payload?.exp) {
          const expiryDate = new Date(decodedToken?.payload?.exp * 1000);
          const now = new Date();
          if (now > expiryDate) {
            isTokenExpired = true;
          }
        }

        if (isExists && !isTokenExpired) {
          this.oktaAuth.tokenManager.add('id_token', idToken);
          this.oktaAuth.tokenManager.add('access_token', accessToken);
          const isNative = this.utilityService.isNativeApp;
          if (isNative) {
            let defaultPage = myAccountRouteWithSlash;
            const lastPage = sessionStorage.getItem('last-page-access');

            if (lastPage !== null && lastPage.toLowerCase().indexOf('claims') > -1) {
              defaultPage = 'claims';
              sessionStorage.removeItem('last-page-access');
            }

            if (lastPage !== null && lastPage.toLowerCase().indexOf('tech-services') > -1) {
              defaultPage = 'tech-services';
              sessionStorage.removeItem('last-page-access');
            }
            this.router.navigate([defaultPage]).then(
              () => { window.location.reload(); });
          } else {
            if (window.location.href.toLowerCase().endsWith('/login?redirect=claims')) {
              sessionStorage.setItem('redirect-to', 'gssp-claims');
              this.router.navigate([dashboardRouteWithSlash]);
            } else {
              this.router.navigate([dashboardRouteWithSlash]).then(() => {
                window.location.reload();
              });
            }
          }
        }
      }
    }
    let sessionPrefetch = this.resourceService.isPortalSettingTrue('Global_Session_Initiate_OnPageLoad');
    if(sessionPrefetch) {
      let sessionId = this.storageService.getLocal('SessionId');
      if(sessionId)
        this.initSession(sessionId.toString());
    }
    let smsRecoveryEnabled = this.resourceService.getPortalSetting('Global_Session_SMSRecovery_Enabled')?.Value.toLowerCase() === 'true';

    // TODO: set this to the current culture that Sitefinity is set to for the app and derive it to a culture that Okta understands (maybe grab the first two characters of the sitefinity culture should work or build a manual map from SF culture to Okta culture).
    //       There are sooo many properties that Okta has that we have to set the language in Okta for users to understand the multitude of error messages that Okta has
    const currentOktaCultureString = 'en';
    const exceedAttempt = this.resourceService.getViewLabel('Login_ExceededMaxAttempts');
    const attemptRemaining = this.resourceService.getViewLabel('Login_AttemptRemaining');
    const showLoginAttemptCounter = this.resourceService.isPortalSettingTrue('Global_ShowLoginAttempt_Counter');
    const lockoutMessage = this.resourceService.getViewLabel('Login_LockedoutMessage');
    const emailNotFoundMessage = this.resourceService.getViewLabel('Login_SignIn_Error_EmailNotFound_Text');

    // Overrides default text that appears in the Okta widget.
    // Here is where you can see the available properties: https://github.com/okta/okta-signin-widget/blob/master/packages/%40okta/i18n/src/properties/login.properties
    const oktaContentOverrides = {
      'primaryauth.title': ' ',
      'primaryauth.submit': this.resourceService.getViewLabel('Login_SignIn_Button_Text'),
      'primaryauth.username.placeholder': this.resourceService.getViewLabel('Login_SignInForm_Email_Label'),
      'primaryauth.password.placeholder': this.resourceService.getViewLabel('Login_SignInForm_Password_Label'),
      forgotpassword: this.resourceService.getViewLabel('Login_ForgotPassword_Link_Text'),
      remember: this.resourceService.getViewLabel('Login_SignInForm_RememberMe_Label'),
      rememberDevice: this.resourceService.getViewLabel('Login_SignInForm_RememberDevice_Label') || 'Remember this device for 15 days',
      'rememberDevice.timebased': this.resourceService.getViewLabel('Login_SignInForm_RememberDevice_Label') || 'Remember this device for 15 days',
      'password.forgot.emailSent.title': this.resourceService.getViewLabel('Login_PasswordReset_EmailSent_Header_Text'),
      'password.forgot.emailSent.desc': this.resourceService.getViewLabel('Login_PasswordReset_EmailSent_Body_Text'),
      'errors.E0000004': this.resourceService.getViewLabel('Login_SignIn_Error_E0000004_Text'),
      'errors.E0000034': this.resourceService.getViewLabel('Login_Error_E0000034_Text'),
      'error.username.required': this.resourceService.getViewLabel('Login_SignIn_Error_UsernameRequired_Text'),
      'error.password.required': this.resourceService.getViewLabel('Login_SignIn_Error_PasswordRequired_Text'),
      'password.reset.title.generic': this.resourceService.getViewLabel('Login_PasswordReset_Header_Text'),
      'password.reset': this.resourceService.getViewLabel('Login_PasswordReset_Header_Text'),
      'password.forgot.email.or.username.placeholder': this.resourceService.getViewLabel('Login_PasswordResetForm_Email_Label'),
      'password.forgot.email.or.username.tooltip': this.resourceService.getViewLabel('Login_PasswordResetForm_Email_Label'),
      'password.forgot.sendEmail': this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'),
      goback: this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'),
      backToSignin: this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'),
      // MFA/OTP related labels
      'mfa.challenge.sms.placeholder': this.resourceService.getViewLabel('Login_MFA_SMS_Placeholder') || 'Enter code',
      'mfa.challenge.email.placeholder': this.resourceService.getViewLabel('Login_MFA_Email_Placeholder') || 'Enter code',
      'mfa.resendCode': this.resourceService.getViewLabel('Login_MFA_ResendCode') || 'Resend code',
      'mfa.sendCode': this.resourceService.getViewLabel('Login_MFA_SendCode') || 'Send code',
      'otp.code.input.placeholder': this.resourceService.getViewLabel('Login_OTP_Code_Placeholder') || 'Enter verification code',
    };

    let usernameAttemptCounts: { [username: string]: { attempts: number, timestamp: number } } = {};
    const maxAttempts = 5;
    this.signIn = new window.OktaSignIn({
      baseUrl: this.configSettings.okta.baseUrl,
      clientId: this.configSettings.okta.clientId,
      redirectUri: selectedSiteSetting.redirectUri,
      language: currentOktaCultureString,
      useClassicEngine: true,
      authParams: {
        //pkce: true,
        //responseMode: 'query',
        //display: 'page',
        scopes: this.configSettings.okta.scopes,
        issuer: this.configSettings.okta.issuer,
        responseType: ['id_token', 'token']
      },
      features: {
        recovery: true,
        selfServiceUnlock: false,
        passwordExpired: true,
        registration: false,
        showPasswordToggleOnSignInPage: true, // Add this
        showPasswordToggleOnRecoveryPage: true, // Add this for recovery pages
        rememberDevice: true, // Enable remember device functionality
        smsRecovery: smsRecoveryEnabled, // Enable SMS recovery/OTP
        emailRecovery: true, // Enable email recovery/OTP
        multiOptionalFactorEnroll: true, // Allow multiple optional factors
        hideSignOutLinkInMFA: false // Show sign out option during MFA
      },
      rememberDevice: {
        // Enable remember device for 15 days (15 * 24 * 60 = 21600 minutes)
        cookieExpiration: 21600, // Duration in minutes (15 days)
        cookieName: 'okta-remember-device', // Optional: custom cookie name
        rememberDeviceLabel: this.resourceService.getViewLabel('Login_SignInForm_RememberDevice_Label')
      },
      i18n: {
        // Overrides default text that appears in the Okta widget.
        // Here is where you can see the available properties: https://github.com/okta/okta-signin-widget/blob/master/packages/%40okta/i18n/src/properties/login.properties
        [currentOktaCultureString]: oktaContentOverrides,
      },
      transformUsername: (username: string) => {
        if (!username.startsWith(appCode.toUpperCase())) {
          username = this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value.toUpperCase() + username;
        }
        try {
          window['OktaErrorMessage'] = '';
          window['lastEmail'] = username;
          usernameAttemptCounts = this.loadUsernameAttemptCountsFromLocalStorage();
          // Check if the attempt count is expired
          const existingAttempt = usernameAttemptCounts[username];
          if (existingAttempt && this.isAttemptCountExpired(existingAttempt)) {
            delete usernameAttemptCounts[username];
            existingAttempt.attempts = 0;
          }

          // Update or create the attempt count
          usernameAttemptCounts[username] = {
            attempts: (existingAttempt?.attempts || 0) + 1,
            timestamp: Date.now()
          };
          const attemptsLeft = maxAttempts - usernameAttemptCounts[username].attempts;
          if (attemptsLeft <= 0) {
            window['OktaErrorMessage'] = exceedAttempt;
          } else {
            window['OktaErrorMessage'] = attemptRemaining.replace('{0}', attemptsLeft.toString());
          }
          // console.log('Current State : ' + username  + " --- " + window['OktaErrorMessage']);
          this.saveUsernameAttemptCountsToLocalStorage(usernameAttemptCounts);
        } catch (e) { }
        return username;
      }
    });

    // Handle recovery token
    const urlParams = new URLSearchParams(window.location.search);
    const recoveryToken = urlParams.get('recovery_token');

    if (recoveryToken) {
      this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.View);
      this.signIn.renderEl(
        {
          el: '#sign-in-widget',
          recoveryToken: recoveryToken,
        },
        (res) => {
          this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.Complete);
          this.oktaAuth.tokenManager.add('id_token', res.tokens.idToken);
          this.oktaAuth.tokenManager.add('access_token', res.tokens.accessToken);
          localStorage.setItem('gtm_login_success', window.location.pathname + '?recovery');

          // Use Angular router for SPA navigation
          this.router.navigate(['/dashboard']).then(() => {
            this.loginService.selectedContractId = undefined;
            this.loginService.selectedPlanNumber = undefined;
          });
        },
        (err) => {
          this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.Failed, {
            message: err?.message ?? 'Password reset failed',
            type: err?.name ?? 'Error',
            code: err?.errorCode ?? err?.status ?? 'Unknown',
          });
          console.error('Recovery error:', err);
        }
      );
    } else {
      // Track MFA method for GTM analytics
      let mfaMethod: string | undefined;

      this.signIn.renderEl({ el: '#sign-in-widget' }, (res) => {
        if (res.status === 'SUCCESS') {
          this.oktaAuth.tokenManager.add('id_token', res.tokens.idToken);
          this.oktaAuth.tokenManager.add('access_token', res.tokens.accessToken);
          localStorage.setItem('gtm_login_success', window.location.pathname);

          if (mfaMethod) {
            localStorage.setItem('login_mfa_method', mfaMethod);
          }

          // Use Angular router for SPA navigation
          this.router.navigate(['/dashboard']).then(() => {
            // Any post-navigation logic can go here if needed
            this.loginService.selectedContractId = undefined;
            this.loginService.selectedPlanNumber = undefined;
          });
        }
      });

      // Listen for afterRender to detect MFA screens and capture the MFA method
      // other is the default for now (we are using only email)
      this.signIn.on('afterRender', (context: { controller: string }) => {
        const controller = context?.controller?.toLowerCase() || '';
        if (controller.includes('mfa-verify') || controller.includes('mfa-challenge')) {
          mfaMethod = 'other';
        }
        // afterRender fires on every screen transition, so the reset-email
        // interceptor is always re-bound to the current (freshly rendered)
        // .email-button node — this is what makes reset work more than once.
        this.interceptResetEmailButton();
      });
    }
    // This is to get around a bug with "Remember Me" functionality
    // When you return to login it transforms username to PG__{username}
    // Setting the value isn't good enough, we have to simulate user entering it
    const appCode = this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value;
    if (this.signIn) {
      this.signIn.on('ready', () => {
        this.oktaPrefix = appCode;
        this.findForgotPasswordButton();
        // The Widget is ready for user input
        this.replaceUsername();
        setTimeout(() => {
           this.attachUsernameEvent();
        }, 1000); // Adjust the delay as needed

        const signInButton = document.querySelector('#okta-signin-submit') as HTMLButtonElement;

        signInButton?.addEventListener('click', e => {
          this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.Login);
          if (window.location.href.toLowerCase().endsWith('/login?redirect=claims')) {
            sessionStorage.setItem('redirect-to', 'gssp-claims');
          } else {
            this.storageService.clearLocal();
          }
        });

        // Monitor for validation errors and disable button accordingly
        this.monitorFormErrors(signInButton);

        const mCallback = (mutations: any) => {
          for (const mutation of mutations) {
            const mutationText: string = mutation.target.textContent;
            const errorText = 'Incorrect email or password';
            if (mutationText.includes(errorText)) {
              if (showLoginAttemptCounter) {
                const errorDiv = document.querySelector('.infobox-error p');
                errorDiv.classList.add('invisible');
                this.accountService.isUserLockedOut(window['lastEmail']).subscribe(timeRemaining => {
                  if (errorDiv) {
                    if (timeRemaining === -1) {
                      errorDiv.innerHTML = emailNotFoundMessage;
                    } else if (timeRemaining === 0) {
                      errorDiv.innerHTML = window['OktaErrorMessage'];
                    } else {
                      errorDiv.innerHTML = lockoutMessage.replace('@minutes', timeRemaining.toString());
                    }
                    errorDiv.classList.remove('invisible');
                  }
                });
              }
              const emitError = {
                message: errorText,
                code: '401',
                type: 'Unauthorized'
              }
              this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginError, emitError);
            }
          }
        };

        const mList = document.querySelector('.o-form-error-container');
        const options = {
          childList: true,
          subtree: true
        };
        const observer = new MutationObserver(mCallback);

        observer.observe(mList, options);
      });
    }

    if (this.loginService.failedLoginBycontract) {
      if (this.loginService.failedLoginByUserGroup) {
        this.loginService.failedLoginBycontract = false;
        this.InitiateAccountNotFoundError();
      } else {
        this.matDialog.closeAll();
        this.loginService.failedLoginBycontract = false;
        this.matDialog.open(FailedLoginModalComponent, {
          data: this.getFailedLoginModalContentData('ContractNotFound'),
        });
      }
    } else if (this.loginService.failedLoginByContractCancalled && this.resourceService.getPortalSetting('Login_Show_FailedLoginModal_For_ContractCancelled').Value.toLowerCase() === 'true') {
      this.matDialog.closeAll();
      this.loginService.failedLoginByContractCancalled = true;
      this.matDialog.open(FailedLoginModalComponent, {
        data: this.getFailedLoginModalContentData('ContractCancelled'),
      });
    }
    else {
      this.InitiateAccountNotFoundError();
    }

    this.createAccountRoute.set(this.resourceService.getPortalSetting('URL_Create_Account').Value);
  }

  // The login page never consumes the `headerKey` query param (it's only read by
  // pages like tech-support / device-inventory). If we land here with one in the
  // URL, strip it through the Router so it doesn't linger or get carried forward.
  // Going through the Router (rather than history.replaceState) keeps Angular's
  // route state in sync, so `queryParamsHandling: 'preserve'` navigations like
  // navigateToSignUp() won't re-attach it. `merge` + `headerKey: null` drops only
  // that param, preserving the rest (e.g. redirect, recovery_token); `replaceUrl`
  // avoids adding a history entry and no reload is triggered.
  private stripHeaderKeyFromUrl(): void {
    if (!this.activatedRoute.snapshot.queryParamMap.has('headerKey')) {
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { headerKey: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private attachUsernameEvent(): void {

    let userSetting = this.resourceService.isPortalSettingTrue("Global_UXOptimization_Enabled");
    if(userSetting == false)
      return;
    const usernameInput = document.getElementById('okta-signin-username') as HTMLInputElement;
    if (usernameInput) {
      // Listen for the blur event in case the user manually changes the field.
      usernameInput.addEventListener('blur', (event: Event) => {
        const input = event.target as HTMLInputElement;
        const email = input.value;
        // console.log('Blur Email: ' + usernameInput.value);
        if (email) {
          this.initSession(email);
        }
      });
      usernameInput.focus();
      usernameInput.blur();
      usernameInput.focus();
    }
  }

  lastEmail = '';
  async initSession(email: string): Promise<void> {
    // track user session
    if (this.lastEmail != email) {
      const data = { SessionId: this.resourceService.getPortalSetting('Global_SiteSettings_OktaPrefix').Value.toUpperCase()  + email };
      this.httpClient.post<string>(this.configService.cmsApiUrl+'/initSession', JSON.stringify(data)).subscribe((data) => {
      });

      this.storageService.setLocal('SessionId', email, true);
      this.lastEmail = email;
    }
  }

  findForgotPasswordButton() {
    this.checkInterval = setInterval(() => {
      const forgotPassword = this.elRef.nativeElement.querySelector('.js-forgot-password');
      if (forgotPassword) {
        this.renderer.listen(forgotPassword, 'click', () => {
          this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_ForgotPassword_Link_Text'), '', null, null);
          this.findBackButton();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  // Re-bindable interceptor for the "Reset via Email" button. Driven by the
  // widget's afterRender event so it always targets the current DOM node and
  // works on every reset attempt — not just the first one. The button may not
  // be in the DOM yet at the instant afterRender fires, so we poll briefly (the
  // same 200ms cadence the other widget lookups use) until it appears, rather
  // than silently giving up and letting Okta's native handler take over.
  interceptResetEmailButton(): void {
    // Clear any in-flight poll first so repeated afterRender events don't stack
    // concurrent timers (each screen transition would otherwise spawn its own).
    clearInterval(this.resetEmailPollInterval);
    if (this.bindResetEmailButton()) {
      return;
    }
    let attempts = 0;
    const maxAttempts = 25; // ~5s at 200ms, matching the other widget lookups
    this.resetEmailPollInterval = setInterval(() => {
      attempts++;
      if (this.bindResetEmailButton() || attempts >= maxAttempts) {
        clearInterval(this.resetEmailPollInterval);
      }
    }, 200);
  }

  // Binds the click interceptor to the current .email-button. Cloning strips
  // Okta's own click handler off the clone; once our /forgotpassword call
  // succeeds we re-fire the (detached) original, which still carries Okta's
  // native recovery handler. Returns true once the button exists (whether we
  // just bound it or it was already intercepted), false if it isn't in the DOM
  // yet so the caller can keep polling.
  private bindResetEmailButton(): boolean {
    const originalButton = this.elRef.nativeElement.querySelector('.email-button') as HTMLButtonElement | null;
    if (!originalButton) {
      return false;
    }
    if (originalButton.dataset['beIntercepted'] === 'true') {
      return true;
    }

    const clonedButton = originalButton.cloneNode(true) as HTMLButtonElement;
    clonedButton.dataset['beIntercepted'] = 'true';
    originalButton.parentNode?.replaceChild(clonedButton, originalButton);

    this.renderer.listen(clonedButton, 'click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();

      const emailInput = this.elRef.nativeElement.querySelector('input[name="username"]') as HTMLInputElement | null;
      const email = emailInput?.value?.trim() ?? '';
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new HttpErrorResponse({
          error: {
            ErrorId: `${format(new Date(), 'yyyyMMdd-HHmmssSSS')}`,
            Source: 'ResetPassword',
            ErrorCode: 'InvalidEmail',
          },
        });
      }

      this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.Start);

      this.requestForgotPassword(email)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            if (response) {
              this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.EmailSent);
              this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'), '', null, null);
            }

            // Give the Okta widget a beat to settle its internal state before
            // re-firing the detached original, which advances it to the
            // "email sent" screen.
            setTimeout(() => {
              originalButton.click();
              if (!response) {
                this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.EmailError, {
                  message: this.resourceService.getViewLabel('Login_PasswordReset_EmailNotSent_Body_Text'),
                  type: 'Error',
                  code: 'Unknown',
                });
                this.replaceEmailSentSubtitle();
              }
            }, 1000);
          },
          error: (err) => {
            this.googleAnalyticsService.emitPasswordResetEvent(GTMPasswordResetEvents.EmailError, {
              message: err?.message ?? 'Reset email request failed',
              type: err?.name ?? 'Error',
              code: err?.status ?? 'Unknown',
            });
            // Backend failed — do not hand off to Okta; leave the user on the reset form.
          },
        });
    });

    return true;
  }

  private requestForgotPassword(email: string): Observable<boolean> {
    const config = window['config'];
    const headers = {
      'Ocp-Apim-Subscription-Key': config.subscriptionKey,
      ParentCMS: config.core.parentCMS,
      ClientCMS: config.core.clientCMS,
      ClientDomain: `${window.location.protocol}//${window.location.hostname}`,
      'Content-Type': 'application/json',
    };
    const body = { factorType: 'Email', username: this.oktaPrefix.toUpperCase() + email };
    // responseType 'text' so an empty / non-JSON 200 body doesn't trigger an
    // HttpClient parse error. We only care that the call succeeded (the old
    // fetch() code likewise never parsed the body), so the response is ignored.
    return this.httpClient.post<boolean>(`${config.core.cmsApiUrl}/forgotpassword?appId=`, body, { headers });
  }

  private replaceEmailSentSubtitle(): void {
    const message = this.resourceService.getViewLabel('Login_PasswordReset_EmailNotSent_Body_Text');
    let attempts = 0;
    const poll = setInterval(() => {
      const subtitleEl = this.elRef.nativeElement.querySelector('p.okta-form-subtitle[data-se="o-form-explain"]') as HTMLElement | null;
      if (subtitleEl) {
        subtitleEl.textContent = message;
        clearInterval(poll);
      } else if (++attempts >= 40) {
        clearInterval(poll);
      }
    }, 50);
  }

  findEmailButton1() {
    this.checkInterval = setInterval(() => {
      const forgotPassword = this.elRef.nativeElement.querySelector('.email-button');
      if (forgotPassword) {
        this.renderer.listen(forgotPassword, 'click', () => {

          console.log('click event triggered');
          this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_Button_Text'), '', null, null);
          //this.findReturnToSigninButton();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  findReturnToSigninButton() {
    this.checkInterval = setInterval(() => {
      const returnToSigninButton = this.elRef.nativeElement.getElementsByClassName('link-button')[0];
      if (returnToSigninButton.getAttribute('data-se') === 'back-button') {
        this.renderer.listen(returnToSigninButton, 'click', () => {
          this.findUsername();
        });
        clearInterval(this.checkInterval);
      }
    }, 200);
  }

  findBackButton() {
    this.checkInterval = setInterval(() => {
      const backButton = this.elRef.nativeElement.querySelector('.js-back');
      if (backButton) {
        this.attachEventToElement(backButton);
        clearInterval(this.checkInterval);
        this.interceptResetEmailButton();
      }
    }, 200);
  }

  findUsername() {
    this.checkInterval = setInterval(() => {
      const usernameInput = this.elRef.nativeElement.querySelector('#okta-signin-username');
      if (usernameInput) {
        this.replaceUsername();
        clearInterval(this.checkInterval);
        this.findForgotPasswordButton();
      }
    }, 200);
  }

  // Centralized fix for the Okta "Remember Me" prefix quirk: a remembered
  // username round-trips back prefixed with the app's Okta prefix (e.g.
  // HWQ__email@x.com), including the ADO 4176818 "Back to sign in" repro,
  // where the value Okta leaves in the field is likewise prefix-shaped.
  // Called once on 'ready' (first paint) and from the monitorFormErrors()
  // MutationObserver on every subsequent re-render — see call sites above.
  // Only acts when the field's value actually changed since we last looked,
  // so it doesn't re-touch the input (and reset cursor position) on every
  // observer tick.
  replaceUsername(): void {
    const usernameInput = document.querySelector('#okta-signin-username') as HTMLInputElement | null;
    if (!usernameInput) return;
    const currentValue = usernameInput.value;
    if (currentValue === this.lastUsernameFieldValue) return;
    if (currentValue.startsWith(this.oktaPrefix.toUpperCase())) {
      usernameInput.value = currentValue.replace(this.oktaPrefix.toUpperCase(), '');
    }
    this.lastUsernameFieldValue = usernameInput.value;
  }

  attachEventToElement(element: HTMLElement): void {
    this.renderer.listen(element, 'click', () => {
      this.googleAnalyticsService.emitClickEvent('click', this.resourceService.getViewLabel('Login_PasswordReset_GoBack_Text'), '', null, null);
    });
  }

  loadUsernameAttemptCountsFromLocalStorage() {
    const storedCounts = localStorage.getItem('usernameAttemptCounts');
    if (storedCounts) {
      if (storedCounts == 'null')
        return {};
      return JSON.parse(storedCounts);
    } else
      return {};
  }

  saveUsernameAttemptCountsToLocalStorage(usernameAttemptCountsData) {
    localStorage.setItem('usernameAttemptCounts', JSON.stringify(usernameAttemptCountsData));
  }

  isAttemptCountExpired(attempt: { timestamp: number }) {
    const currentTime = Date.now();
    const expirationTime = attempt.timestamp + 30 * 60 * 1000; // 30 minutes in milliseconds
    return currentTime > expirationTime;
  }

  private getFailedLoginModalContentData(failedLoginModalName: 'ContractNotFound' | 'ContractCancelled' | 'AccountNotFound'): INotFoundModal {
    const emitError = {
      message: this.resourceService.getViewLabel(`Login_FailedLoginModal_Title_For_${failedLoginModalName}`),
      type: 'ContractError',
      code: failedLoginModalName,
    }
    this.googleAnalyticsService.emitLoginEvent(GTMLoginEvents.LoginError, emitError);
    return {
      Title: this.resourceService.getViewLabel(`Login_FailedLoginModal_Title_For_${failedLoginModalName}`),
      Content: this.resourceService.transformContentData(this.resourceService.getViewLabel(`Login_FailedLoginModal_Body_Text_For_${failedLoginModalName}`)),
      ButtonLabel: this.resourceService.getViewLabel(`Login_FailedLoginModal_Button_Text_For_${failedLoginModalName}`),
      Id: failedLoginModalName,
    };
  }

  InitiateAccountNotFoundError() {
    if (this.loginService.failedLoginByUserGroup) {
      this.matDialog.closeAll();
      this.loginService.failedLoginByUserGroup = false;
      this.matDialog.open(FailedLoginModalComponent, {
        data: this.getFailedLoginModalContentData('AccountNotFound'),
      });
    }
  }

  async getToken() {
    return this.oktaAuth.token.getWithoutPrompt({
      scopes: this.configSettings.okta.scopes,
      issuer: this.configSettings.okta.issuer,
    }).then((res) => {
      return Promise.resolve({ isValid: true, token: res });
    }).catch(err => {
      return Promise.resolve({ isValid: false, token: null });
    });
  }

  isValidateToken(oktTokenStorage: string) {
    const tokens = JSON.parse(oktTokenStorage);
    const idToken = tokens.id_token ? tokens.id_token : tokens.idToken;
    const accessToken = tokens.access_token ? tokens.access_token : tokens.accessToken;
    const token = tokens.access_token && tokens.access_token.accessToken ? tokens.access_token.accessToken
      : (tokens.accessToken ? tokens.accessToken.accessToken : null);
    let isValid = false;
    if (idToken && accessToken && token) {
      isValid = true;
    }
    return isValid;
  }

  private monitorFormErrors(signInButton: HTMLButtonElement): void {
    if (!signInButton) return;

    let isChecking = false;

    const checkForErrors = () => {
      // Debounce to prevent excessive calls
      if (isChecking) return;

      isChecking = true;

      setTimeout(() => {
        const usernameInput = document.querySelector('#okta-signin-username') as HTMLInputElement;
        const passwordInput = document.querySelector('#okta-signin-password') as HTMLInputElement;

        // Check if any error elements exist with the okta-form-input-error class
        const errorElements = document.querySelectorAll('.okta-form-input-error');
        const hasErrors = errorElements.length > 0;

        // Check if username or password are empty
        const username = usernameInput?.value?.trim() || '';
        const password = passwordInput?.value?.trim() || '';
        const isEmpty = username === '' || password === '';
        const isAutofill = this.usernameAutofilled && this.passwordAutofilled;
        if (!isAutofill && (hasErrors || isEmpty)) {
          signInButton.disabled = true;
          signInButton.classList.add('link-button-disabled');
        } else {
          signInButton.disabled = false;
          signInButton.classList.remove('link-button-disabled');
        }

        isChecking = false;
      }, 1000);
    };

    // Disable button initially
    signInButton.disabled = true;
    signInButton.classList.add('link-button-disabled');

    // Monitor for changes in the form with debouncing
    const formContainer = document.querySelector('#okta-sign-in');
    if (formContainer) {
      const observer = new MutationObserver(() => {
        checkForErrors();
        // Single centralized detection point for the Okta prefix-stripping
        // fix — fires on every re-render Okta does under #okta-sign-in
        // (e.g. Forgot Password -> Back to sign in), so no separate
        // click-triggered poll is needed. See replaceUsername().
        this.replaceUsername();
      });

      observer.observe(formContainer, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Check on blur and input events
    const usernameInput = document.querySelector('#okta-signin-username');
    const passwordInput = document.querySelector('#okta-signin-password');

    usernameInput?.addEventListener('blur', checkForErrors);
    passwordInput?.addEventListener('blur', checkForErrors);
    usernameInput?.addEventListener('input', checkForErrors);
    passwordInput?.addEventListener('input', checkForErrors);

    this.usernameAutofillSubscription = this.autofillMonitor.monitor(usernameInput!).subscribe(e => this.checkAutoFill('username', e.isAutofilled));
    this.passwordAutofillSubscription = this.autofillMonitor.monitor(passwordInput!).subscribe(e => this.checkAutoFill('password', e.isAutofilled));
  }

  cancelAutoFillMonitoring() {
    // Unsubscribe from autofill monitors
    if (this.usernameAutofillSubscription) {
      this.usernameAutofillSubscription.unsubscribe();
    }
    if (this.passwordAutofillSubscription) {
      this.passwordAutofillSubscription.unsubscribe();
    }
  }

  checkAutoFill(field: 'username' | 'password', status: boolean) {

    // Track autofill status for each field
    if (field === 'username') {
      this.usernameAutofilled = status;
    } else if (field === 'password') {
      this.passwordAutofilled = status;
    }
  }

  navigateToSignUp() {
    this.router.navigate([this.resourceService.getPortalSetting('URL_Create_Account').Value], { queryParamsHandling: 'preserve' });
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }

  ngOnDestroy(): void {
    clearInterval(this.checkInterval);
    clearInterval(this.resetEmailPollInterval);
    this.lastUsernameFieldValue = '';
    this.signIn.remove();
    this.cancelAutoFillMonitoring();
  }
}
